import * as _ from 'lodash';
import React from 'react';
import {useState, useMemo} from 'react';
import ModifiedDropdown from '../elements/ModifiedDropdown';
import * as Types from './types';
import PageControls from './ControlPage';
import LabeledOption from '../elements/LabeledOption';
import * as Table from './tableState';
import * as CGReact from './cg/cgreact';
import {useGatedValue} from '../../state/hooks';

import * as Panel2 from './panel';
import {PanelComp2} from './PanelComp';

export interface PanelMultiContainerConfig {
  pageSize?: number;
  childConfig: any;
}
type PanelMultiContainerProps = Panel2.PanelConverterProps;

const PanelMultiContainerConfig: React.FC<PanelMultiContainerProps> = props => {
  const {config, updateConfig} = props;
  return (
    <div>
      <LabeledOption
        label="Page size"
        option={
          <ModifiedDropdown
            style={{marginRight: 8}}
            selection
            value={config.pageSize || 3}
            options={[1, 2, 3, 5, 10].map(o => ({
              key: o,
              value: o,
              text: o,
            }))}
            onChange={(e, {value}) => updateConfig({pageSize: value as number})}
          />
        }
      />
      <PanelComp2
        input={props.input}
        inputType={Spec.convert(props.inputType)}
        loading={props.loading}
        panelSpec={props.child}
        configMode={true}
        config={props.config}
        context={props.context}
        updateConfig={props.updateConfig}
        updateContext={props.updateContext}
      />
    </div>
  );
};

const PanelMultiContainer: React.FC<PanelMultiContainerProps> = props => {
  const {input, config} = props;
  const rowsNode = input.path;
  const [pageNum, setPageNum] = useState(0);
  const pageSize = config.pageSize ?? 3;
  const visibleRowsNode = useMemo(
    () => Table.getPagedRowsNode(pageSize, pageNum, rowsNode),
    [pageSize, pageNum, rowsNode]
  );
  const rowNodesUse = CGReact.useEach(visibleRowsNode as any);
  const rowNodes = rowNodesUse.result;

  return useGatedValue(
    <div style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
      <div
        style={{
          height: 'calc(100% - 32px)',
          display: 'flex',
          justifyContent: 'space-evenly',
          flexDirection: 'row',
        }}>
        {_.range(pageSize).map(offset => {
          const node = rowNodes[offset];
          if (node == null) {
            return <div key={offset} style={{flexGrow: 1}} />;
          }
          return (
            <div
              key={offset}
              style={{marginRight: 8, flexBasis: 1, flexGrow: 1}}>
              <PanelComp2
                input={{path: node}}
                inputType={node.type}
                loading={props.loading}
                panelSpec={props.child}
                configMode={false}
                config={props.config}
                context={props.context}
                updateConfig={props.updateConfig}
                updateContext={props.updateContext}
              />
            </div>
          );
        })}
      </div>
      <div>
        <PageControls
          rowsNode={rowsNode}
          page={pageNum}
          pageSize={pageSize}
          nextPage={() => setPageNum(pageNum + 1)}
          prevPage={() => setPageNum(pageNum - 1)}
        />
      </div>
    </div>,
    o => !rowNodesUse.loading
  );
};

export const Spec: Panel2.PanelConvertSpec = {
  id: 'multi-container',
  displayName: 'Paged Objects',
  ConfigComponent: PanelMultiContainerConfig,
  Component: PanelMultiContainer,
  convert: (inputType: Types.Type) => {
    if (Types.isSimpleType(inputType)) {
      return null;
    }
    if (
      inputType.type === 'array' &&
      (inputType.objectType as Types.File).type !== 'file'
    ) {
      return inputType.objectType;
    }
    if (inputType.type === 'dict') {
      if (Types.isSimpleType(inputType.objectType)) {
        return null;
      }
      if (inputType.objectType.type === 'array') {
        return {
          type: 'dict' as const,
          objectType: inputType.objectType.objectType,
        };
      }
    }
    return null;
  },
  defaultFixedSize: childDims => ({
    width: childDims.width != null ? childDims.width * 3 + 20 : undefined,
    height: childDims.height != null ? childDims.height + 40 : undefined,
  }),
};
